import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Clock, Leaf, ArrowLeft, Star, Home, Heart, Search, Loader2 } from "lucide-react";
import { getSportColor } from "@/lib/sports";
import SportAutocomplete from "@/components/SportAutocomplete";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import { KycBookingNotice } from "@/components/KycBookingNotice";
import { getBookingKycMessage, hasApprovedBookingKyc } from "@/lib/bookingKyc";

export default function BookCourt() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courts, setCourts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchMode, setSearchMode] = useState(false);
  const [sport, setSport] = useState("");
  const [city, setCity] = useState("");

  useEffect(() => { fetchSmartCourts(); }, []);

  const fetchSmartCourts = async () => {
    try {
      const res = await api.get("/player/smart-courts");
      setCourts(res.data);
    } catch { setCourts({ recently_booked: [], favorites: [], nearby: [] }); }
    setLoading(false);
  };

  const searchCourts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (sport) params.sport_type = sport;
      if (city) params.city = city;
      const res = await api.get("/venues", { params });
      setCourts({ recently_booked: [], favorites: [], nearby: res.data });
    } catch { toast.error("Search failed"); }
    setLoading(false);
  };

  const setHomeCourt = async (venueId) => {
    try {
      await api.put("/player/home-court", { venue_id: venueId });
      toast.success("Home court set!");
      fetchSmartCourts();
    } catch { toast.error("Failed to set home court"); }
  };

  const toggleFavorite = async (venueId, isFav) => {
    try {
      await api.post("/player/favorite-court", { venue_id: venueId, action: isFav ? "remove" : "add" });
      toast.success(isFav ? "Removed from favorites" : "Added to favorites!");
      fetchSmartCourts();
    } catch { toast.error("Failed to update favorites"); }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const favIds = new Set((courts?.favorites || []).map(v => v.venue_id));
  const homeId = user?.home_court_id;
  const kycBlocked = user && !hasApprovedBookingKyc(user);
  const kycMessage = getBookingKycMessage(user);

  const VenueCard = ({ venue, section }) => (
    <Card className="overflow-hidden hover:-translate-y-1 transition-transform cursor-pointer" onClick={() => navigate(`/venues/${venue.venue_id}`)}>
      <div className="h-32 overflow-hidden relative">
        <img src={venue.images?.[0] || "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400"} alt={venue.name} className="w-full h-full object-cover" />
        <div className="absolute top-2 right-2 flex gap-1">
          <button onClick={(e) => { e.stopPropagation(); setHomeCourt(venue.venue_id); }}
            className={`w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-sm ${homeId === venue.venue_id ? "bg-primary text-white" : "bg-white/80 text-muted-foreground hover:text-primary"}`}
            title="Set as Home Court">
            <Home className="w-3.5 h-3.5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); toggleFavorite(venue.venue_id, favIds.has(venue.venue_id)); }}
            className={`w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-sm ${favIds.has(venue.venue_id) ? "bg-red-500 text-white" : "bg-white/80 text-muted-foreground hover:text-red-500"}`}
            title="Toggle Favorite">
            <Heart className="w-3.5 h-3.5" fill={favIds.has(venue.venue_id) ? "currentColor" : "none"} />
          </button>
        </div>
        {venue.esg_grade && (
          <Badge className="absolute bottom-2 left-2 bg-green-600 text-white text-[9px]"><Leaf className="w-2.5 h-2.5 mr-0.5" />ESG {venue.esg_grade}</Badge>
        )}
      </div>
      <CardContent className="p-3">
        <div className="flex gap-1 mb-1 flex-wrap">
          {(venue.sport_types || []).map(s => <Badge key={s} variant="outline" className={`text-[9px] capitalize ${getSportColor(s)}`}>{s}</Badge>)}
        </div>
        <h3 className="font-heading text-sm font-bold uppercase tracking-tight truncate">{venue.name}</h3>
        <p className="text-xs text-muted-foreground flex items-center gap-0.5 mt-0.5"><MapPin className="w-3 h-3" />{venue.city}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="book-court-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="font-heading text-2xl sm:text-3xl">Book a Court</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => setSearchMode(!searchMode)} data-testid="toggle-search">
          <Search className="w-4 h-4 mr-1" />{searchMode ? "Smart View" : "Search All"}
        </Button>
      </div>

      {kycBlocked && (
        <KycBookingNotice message={kycMessage} compact testId="book-court-kyc-notice" />
      )}

      {/* Search mode */}
      {searchMode && (
        <div className="flex gap-3 items-end flex-wrap">
          <SportAutocomplete value={sport} onValueChange={setSport} placeholder="Sport" triggerClassName="w-48" />
          <LocationAutocomplete value={city} onValueChange={setCity} placeholder="Location" triggerClassName="w-48" />
          <Button onClick={searchCourts} className="rounded-full"><Search className="w-4 h-4 mr-1" />Search</Button>
        </div>
      )}

      {!searchMode && (
        <>
          {/* Recently Booked */}
          {(courts?.recently_booked || []).length > 0 && (
            <div>
              <h2 className="font-heading text-base uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Recently Booked</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {courts.recently_booked.map(v => <VenueCard key={v.venue_id} venue={v} section="recent" />)}
              </div>
            </div>
          )}

          {/* Favorites */}
          {(courts?.favorites || []).length > 0 && (
            <div>
              <h2 className="font-heading text-base uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2"><Heart className="w-4 h-4 text-red-500" /> My Favorites</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {courts.favorites.map(v => <VenueCard key={v.venue_id} venue={v} section="favorites" />)}
              </div>
            </div>
          )}
        </>
      )}

      {/* Nearby / Search Results */}
      <div>
        <h2 className="font-heading text-base uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4" /> {searchMode ? "Search Results" : "Courts For You"}
        </h2>
        {(courts?.nearby || []).length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No courts found matching your preferences</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(courts?.nearby || []).map(v => <VenueCard key={v.venue_id} venue={v} section="nearby" />)}
          </div>
        )}
      </div>
    </div>
  );
}
